import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { CircularProgress } from '@mui/material';
import useOffers from 'api/useOffers';
import { Offer, type OfferUI, type ProductsSequence } from 'common/contracts';
import { TRIMMED_INPUT_REGEX } from 'constants/constants';
import { EActiveState, OfferType } from 'constants/enums';
import { ProgressBar } from 'design-system/AcSidebar/constants';
import { useFormik } from 'formik';
import { motion } from 'framer-motion';
import { useTabTracking } from 'hooks/useTabTracking';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { EToasterStatus, showToast } from 'utils/showToast';
import { STATUS_OPTIONS } from 'views/CheckoutDesign/types';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';
import * as yup from 'yup';

import FormHeader from 'components/FormHeader/FormHeader';
import FormSectionsTabs from 'components/FormSectionsTabs';
import { Tabs } from 'components/ui/tabs';

import { EProgressBarTabs, type IProgressBarFormProps } from '../ProgressBars.types';
import {
  getProductsSequenceValidationSchema,
  isMilestoneProgressBarDesign,
  mapMissionsToProductsSequencePayload,
  mapProductsSequenceToMissions
} from '../ProgressBars.utils';

import { ProgressBarPreview } from './ProgressBarPreview';
import ConfigurationTab from './Tabs/ConfigurationTab';
import ScheduleTab from './Tabs/ScheduleTab';
import SettingsTab from './Tabs/SettingsTab';

const ProgressBarForm: FC<IProgressBarFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { progressBarId, publisherId } = useParams();
  const navigate = useNavigate();
  const { updateOffer, addOffer, getOffer, getOffers } = useOffers({
    offerId: progressBarId,
    offerType: OfferType.PROGRESS_BAR
  });

  const [activeTab, setActiveTab] = useState<EProgressBarTabs>(
    EProgressBarTabs.SETTINGS
  );
  const [data, setData] = useState<Partial<Offer>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [active, setActive] = useState<EActiveState>(
    data?.active ? EActiveState.ACTIVE : EActiveState.INACTIVE
  );
  const { defaultOfferDesignId, currentOfferUI, setCurrentOfferUI, getOffersUI } =
    useOfferDesignOptions({
      publisherId: publisherId || '',
      offerDesignType: OfferDesignTypes.ProgressBar,
      offerId: progressBarId
    });
  const [showPreview, setShowPreview] = useState<boolean>(true);

  const tabTitles = Object.values(EProgressBarTabs);

  useEffect(() => {
    if (progressBarId && publisherId) {
      getOffer.refetch();
    }
  }, [progressBarId, publisherId]);

  useEffect(() => {
    if (progressBarId && !getOffer.isLoading && getOffer.data) {
      setData(getOffer.data.result);
    }
  }, [getOffer.data, getOffer.isLoading, progressBarId]);

  useEffect(() => {
    if (data) {
      setIsLoading(false);
    }
  }, [data]);

  const initialProductsSequence = useMemo(
    () =>
      mapProductsSequenceToMissions(
        data?.productsSequence as ProductsSequence[] | undefined
      ),
    [data?.productsSequence]
  );

  const checkUniqueness = useCallback(
    (value: string | undefined, field: keyof Offer) => {
      const isTaken = getOffers.data?.offers.find((o: Offer) => {
        if (!edit) return o[field] === value;
        return o[field] === value && data[field] !== value;
      });
      return !isTaken;
    },
    [edit, data, getOffers.data]
  );

  const {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue
  } = useFormik({
    validateOnMount: true,
    validationSchema: () =>
      yup.object().shape({
        name: yup
          .string()
          .matches(TRIMMED_INPUT_REGEX, 'Name should not be spaces')
          .min(3, `Name length should be longer`)
          .required('Name is required')
          .test('uniqueness', 'Name already exists', (value) => {
            return checkUniqueness(value, 'name');
          }),
        publisherOfferId: yup
          .string()
          .matches(TRIMMED_INPUT_REGEX, 'External ID should not be spaces')
          .required('External ID is required')
          .test('uniqueness', 'External ID already exists', (value) => {
            return checkUniqueness(value, 'publisherOfferId');
          }),
        offerUiId: yup.string().required('Offer Design is required'),
        productsSequence: getProductsSequenceValidationSchema(isMilestoneDesign)
      }),
    enableReinitialize: true,
    initialValues: {
      publisherOfferId: data?.publisherOfferId || '',
      name: data?.name ? `${data.name}${dup ? '_copy' : ''}` : '',
      type: data?.type || OfferType.PROGRESS_BAR,
      offerUiId:
        data.offerUi?._id || data.offerUi?.offerUiId || defaultOfferDesignId,
      active:
        data?.active === undefined || data?.active === null
          ? EActiveState.ACTIVE
          : data.active,
      segments: data?.segments || [],
      schedule: data?.schedule || { permanent: true, timeFrames: [] },
      productsSequence: initialProductsSequence
    },
    onSubmit: async (values) => {
      const newProgressBar: Partial<Offer> & { sectionId?: string | null } = {
        ...values,
        active: active === EActiveState.ACTIVE,
        productsSequence: mapMissionsToProductsSequencePayload(
          values.productsSequence as ProductsSequence[]
        ),
        schedule: {
          ...values.schedule,
          timeFrames: values.schedule.permanent
            ? []
            : values.schedule.timeFrames
        }
      };
      const isUpdate = edit && progressBarId && !dup;
      const action = isUpdate ? updateOffer : addOffer;
      const args: any = isUpdate
        ? { offerId: progressBarId, form: newProgressBar }
        : newProgressBar;

      await action.mutateAsync(args, {
        onSuccess: () => {
          getOffers.refetch();
          navigate('../');
          showToast({
            message: `Progress Bar ${isUpdate ? 'edited' : 'added'} successfully`,
            status: EToasterStatus.SUCCESS
          });
        },
        onError: () => {
          showToast({
            message: `Error ${isUpdate ? 'editing' : 'adding'} Progress Bar`,
            status: EToasterStatus.ERROR
          });
        }
      });
    }
  });

  useEffect(() => {
    setActive(values.active ? EActiveState.ACTIVE : EActiveState.INACTIVE);
  }, [values?.active]);

  useEffect(() => {
    setCurrentOfferUI(values.offerUiId);
  }, [values.offerUiId]);

  const isMilestoneDesign = useMemo(
    () =>
      isMilestoneProgressBarDesign(
        getOffersUI.data?.result?.find(
          (design: OfferUI) => design._id === values.offerUiId
        )
      ),
    [getOffersUI.data?.result, values.offerUiId]
  );

  useUnsavedChanges({ dirty });

  const progressBarTabs = tabTitles.map((title) => ({
    label: title,
    value: title
  }));

  const { handleTabChange } = useTabTracking({
    sidebarTab: ProgressBar.title,
    tabs: progressBarTabs,
    customNavigate: (value) => setActiveTab(value as EProgressBarTabs)
  });

  const formikProps = {
    values,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    errors,
    touched,
    setFieldValue
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white fixed h-screen w-full top-0 left-0 z-[1000] overflow-hidden"
    >
      <FormHeader
        closeFormOnSubmit={false}
        onCloseForm={() => navigate('../')}
        onMainButtonClick={() => submitForm()}
        onOptionChange={(status) =>
          setFieldValue('active', status === EActiveState.ACTIVE)
        }
        selectedOption={active}
        options={STATUS_OPTIONS}
        title={`${edit ? 'Edit' : 'Create'} Progress bar`}
        isFormValid={
          !isLoading &&
          isValid &&
          (!edit || dirty) &&
          (values.schedule?.permanent || !!values.schedule?.timeFrames?.length)
        }
        mainButtonText="Save"
        secondaryButtonText={showPreview ? 'Hide Preview' : 'Show Preview'}
        onSecondaryButtonClick={() => setShowPreview((prev) => !prev)}
      />
      <div className="flex h-[calc(100vh-73px)]">
        <div
          className={`flex flex-col overflow-y-auto px-3 ${showPreview ? 'w-1/2' : 'w-full'}`}
        >
          <div className="w-[528px] mx-auto">
            <Tabs
              className="h-full pt-10 pb-2 w-[528px]"
              value={activeTab}
              onValueChange={handleTabChange}
            >
              <FormSectionsTabs
                currentTab={activeTab}
                tabs={tabTitles.map((title) => ({
                  value: title,
                  label: title,
                  disabled: getOffer.isLoading && edit
                }))}
              />
            </Tabs>
          </div>
          {progressBarId && getOffer.isLoading ? (
            <CircularProgress className="absolute top-1/2 left-1/2 size-16" />
          ) : (
            <div className="mt-7 pb-7 w-full mx-auto">
              {activeTab === EProgressBarTabs.SETTINGS && (
                <SettingsTab
                  formikProps={formikProps}
                  currentOfferUI={currentOfferUI}
                />
              )}

              {activeTab === EProgressBarTabs.CONFIGURATION && (
                <ConfigurationTab
                  formikProps={formikProps}
                  isMilestoneDesign={isMilestoneDesign}
                />
              )}

              {activeTab === EProgressBarTabs.SCHEDULE && (
                <ScheduleTab formikProps={formikProps} />
              )}
            </div>
          )}
        </div>
        {showPreview && (
          <ProgressBarPreview
            currentOfferUI={currentOfferUI}
            productsSequence={values.productsSequence as ProductsSequence[]}
          />
        )}
      </div>
    </motion.div>
  );
};

export default ProgressBarForm;
